import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Button, 
  Chip,
  Container,
  Paper,
  IconButton
} from '@mui/material';
import { 
  Favorite as FavoriteIcon, 
  FavoriteBorder as FavoriteBorderIcon,
  Download as DownloadIcon,
  ShoppingCart as CartIcon
} from '@mui/icons-material';
import { ProductService } from '../services/productService';
import { Product } from '../types';

interface FavoritesProps {
  onAddToCart: (product: Product) => void;
  onDownload: (productId: string) => void;
}

const Favorites: React.FC<FavoritesProps> = ({ onAddToCart, onDownload }) => {
  const [favorites, setFavorites] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<string | null>(null);
  const productService = new ProductService();

  useEffect(() => {
    loadFavorites();
  }, []);

  const loadFavorites = async () => {
    try {
      setLoading(true);
      const response = await productService.getFavorites();
      if (response.success && response.data) {
        // Map the API response to match Product type
        const mappedProducts = (response.data.products || []).map((product: any) => ({
          id: product.productId || product.id,
          name: product.name,
          description: product.description,
          price: product.price,
          image: product.previewUrl || product.image,
          category: product.category,
          rating: product.rating,
          downloads: product.downloads,
          tags: product.tags || [],
          author: product.author || 'Unknown',
          fileSize: product.fileSize,
          format: product.format || 'SVG'
        }));
        setFavorites(mappedProducts);
      }
    } catch (error) {
      console.error('Error loading favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFavorite = async (productId: string) => {
    try {
      await productService.removeFromFavorites(productId);
      setFavorites(prev => prev.filter(product => product.id !== productId));
    } catch (error) {
      console.error('Error removing favorite:', error);
    }
  };

  const handleDownload = async (productId: string) => {
    setDownloading(productId);
    try {
      await onDownload(productId);
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h6" sx={{ textAlign: 'center' }}>
          Loading favorites...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper 
        sx={{ 
          p: 4, 
          bgcolor: 'background.paper', 
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid',
          borderColor: 'grey.200'
        }}
      >
        {/* Section Title */}
        <Typography 
          variant="h4" 
          sx={{ 
            mb: 1, 
            fontWeight: 700,
            textAlign: 'center',
            background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
        >
          My Favorites
        </Typography>
        
        <Typography 
          variant="h6" 
          sx={{ 
            mb: 4, 
            textAlign: 'center',
            color: 'text.secondary',
            fontWeight: 400
          }}
        >
          Your saved SVG designs
        </Typography>

        {favorites.length > 0 ? (
          <Grid container spacing={4}>
            {favorites.map((product) => (
              <Grid item xs={12} sm={6} md={4} key={product.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: 'background.paper',
                    boxShadow: 3,
                    borderRadius: 3,
                    p: 2,
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: 6 },
                  }}
                >
                  <Box sx={{ position: 'relative' }}>
                    <img
                      src={product.image}
                      alt={product.name}
                      style={{
                        width: '100%',
                        height: '200px',
                        objectFit: 'cover',
                        borderRadius: '8px'
                      }}
                    />
                    <IconButton
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' }
                      }}
                      onClick={() => handleRemoveFavorite(product.id)}
                    >
                      <FavoriteIcon color="error" />
                    </IconButton>
                  </Box>
                  
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 0 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                      {product.name}
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
                      {product.description.length > 80 
                        ? product.description.substring(0, 80) + '...' 
                        : product.description}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      <Chip size="small" label={`$${product.price}`} color="primary" />
                      <Chip size="small" label={product.fileSize} />
                      <Chip size="small" label={`⭐ ${product.rating}`} />
                      <Chip size="small" label={product.category} variant="outlined" />
                    </Box>

                    <Box sx={{ mt: 'auto', display: 'flex', gap: 1 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<CartIcon />}
                        onClick={() => onAddToCart(product)}
                        sx={{ fontWeight: 600 }}
                      >
                        Add to Cart
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownload(product.id)}
                        disabled={downloading === product.id}
                      >
                        {downloading === product.id ? 'Downloading...' : 'Download'}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ 
            textAlign: 'center', 
            py: 8,
            bgcolor: 'grey.50',
            borderRadius: 3,
            border: '2px dashed',
            borderColor: 'grey.300'
          }}>
            <FavoriteBorderIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
              No favorites yet
            </Typography>
            <Typography color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
              Start exploring our SVG collection and save your favorite designs here!
            </Typography>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default Favorites; 